import React, { useEffect, useRef, useCallback } from 'react';
import { storyData } from '../data/mockData';

const StorySection = () => {
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const threadRef = useRef(null);

  const updateThread = useCallback(() => {
    const timeline = timelineRef.current;
    const thread = threadRef.current;
    if (!timeline || !thread) return;

    const rect = timeline.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const timelineTop = rect.top;
    const timelineHeight = rect.height;

    if (timelineTop > windowHeight) {
      thread.style.setProperty('--progress', '0');
      thread.classList.remove('is-animating');
      return;
    }

    thread.classList.add('is-animating');
    const scrolled = windowHeight - timelineTop;
    const progress = Math.min(Math.max(scrolled / timelineHeight, 0), 1);
    thread.style.setProperty('--progress', progress.toString());

    const dots = timeline.querySelectorAll('.timeline-dot');
    dots.forEach((dot) => {
      const dotRect = dot.getBoundingClientRect();
      if (dotRect.top < windowHeight * 0.75) {
        dot.classList.add('is-visible');
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    window.addEventListener('scroll', updateThread, { passive: true });
    updateThread();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateThread);
    };
  }, [updateThread]);

  return (
    <section id="story" className="py-20 md:py-28 bg-white" ref={sectionRef}>
      {/* Section Header */}
      <div className="text-center mb-16 md:mb-20 animate-on-scroll animate-fade-up">
        <h4 className="font-script text-3xl md:text-4xl text-gray-400 mb-3">Our Story</h4>
        <h2 className="font-heading text-3xl md:text-[40px] text-gray-800 mb-6">How it Happened</h2>
        <div className="w-20 h-[1.5px] bg-olive/40 mx-auto"></div>
      </div>

      {/* Timeline */}
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="relative" ref={timelineRef}>
          {/* Golden thread */}
          <div className="timeline-thread h-full" ref={threadRef} />

          {storyData.map((story, index) => (
            <div
              key={story.id}
              className={`relative flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-10 lg:gap-20 mb-20 last:mb-0 animate-on-scroll ${index % 2 === 0 ? 'animate-fade-left' : 'animate-fade-right'}`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Golden dot marker */}
              <div className="timeline-dot" style={{ top: '50%' }} />

              {/* Image */}
              <div className="w-full lg:w-1/2 pl-8 lg:pl-0">
                <div className="relative group">
                  <div className="absolute inset-4 border border-olive/20 z-20 pointer-events-none transition-all duration-500 group-hover:inset-6"></div>
                  <img
                    src={story.image}
                    alt={story.title}
                    className="w-full h-[320px] md:h-[420px] object-cover relative z-10"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`w-full lg:w-1/2 pl-8 lg:pl-0 ${index % 2 === 0 ? 'lg:pl-2' : 'lg:pr-2'}`}>
                <h3 className="font-script text-4xl md:text-5xl text-gray-800 mb-4 leading-tight">{story.title}</h3>
                <p className="font-body text-olive/80 text-sm tracking-wide mb-5">{story.date}</p>
                <p className="font-body text-gray-500 leading-[1.8] text-[15px] whitespace-pre-line">{story.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StorySection;
